import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export interface NotificationConfig {
  title: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
  duration?: number;
  icon?: string;
}

export interface NotificationState {
  notifications: (NotificationConfig & { id: string })[];
}

@Injectable({
  providedIn: "root",
})
export class CustomNotificationService {
  private notificationState = new BehaviorSubject<NotificationState>({
    notifications: [],
  });

  public notificationState$ = this.notificationState.asObservable();

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  show(config: NotificationConfig) {
    const notification = {
      ...config,
      id: this.generateId(),
      duration: config.duration || 5000,
    };

    const currentState = this.notificationState.value;
    this.notificationState.next({
      notifications: [...currentState.notifications, notification],
    });

    if (notification.duration > 0) {
      setTimeout(() => {
        this.remove(notification.id);
      }, notification.duration);
    }
  }

  success(title: string, message: string, duration?: number) {
    this.show({
      title,
      message,
      type: "success",
      icon: "circle-check",
      duration,
    });
  }

  error(title: string, message: string, duration?: number) {
    this.show({
      title,
      message,
      type: "error",
      icon: "circle-x",
      duration,
    });
  }

  warning(title: string, message: string, duration?: number) {
    this.show({
      title,
      message,
      type: "warning",
      icon: "alert-triangle",
      duration,
    });
  }

  info(title: string, message: string, duration?: number) {
    this.show({
      title,
      message,
      type: "info",
      icon: "info",
      duration,
    });
  }

  remove(id: string) {
    const currentState = this.notificationState.value;
    this.notificationState.next({
      notifications: currentState.notifications.filter((n) => n.id !== id),
    });
  }

  clear() {
    this.notificationState.next({
      notifications: [],
    });
  }
}
